import {
  isBridgeEnvelope,
  NATIVE_BRIDGE_VERSION,
  type BiltBridgeEnvelope,
  type BiltNativeEvent,
  type BiltNativeRequest,
} from './nativeBridgeWeb';

export type NativeCapabilities = {
  native_bridge_version: number;
  platform: 'ios' | 'android';
  app_version: string;
  qr_scan: boolean;
  gate_scan: boolean;
  ticket_scan: boolean;
  native_camera: boolean;
  native_photo_spots?: boolean;
  layoutGeometry?: boolean;
  systemNavigation?: boolean;
  displayKeepAwake?: boolean;
  native_share: boolean;
  haptics: boolean;
  push: boolean;
  offline_events: boolean;
  system_settings: boolean;
  native_files: boolean;
  native_image_save?: boolean;
  native_image_jpeg?: boolean;
  native_image_svg?: boolean;
  nfc: boolean;
  tap_to_pay: boolean;
};

export type ScanMode = 'auto' | 'passport' | 'gate' | 'ticket' | 'campaign' | 'url';

export type PhotoPurpose =
  | 'vehicle_gallery'
  | 'maintenance_receipt'
  | 'avatar'
  | 'entity_logo'
  | 'passport_spot'
  | 'passport_memory'
  | 'generic';

export type PhotoBridgePayload = {
  photos?: Array<{ uri: string; uploadId: number; focalX?: number; focalY?: number }>;
  errors?: string[];
  ok: boolean;
  uri?: string;
  uploadId?: string | number;
  focalX?: number;
  focalY?: number;
  error?: string;
};

declare global {
  interface Window {
    __BILT_NATIVE_SHELL__?: {
      version: number;
      platform: 'ios' | 'android';
      capabilities: NativeCapabilities;
    };
    ReactNativeWebView?: {
      postMessage: (message: string) => void;
    };
  }
}

export function isRunningInBiltNativeShell(): boolean {
  return Boolean(window.__BILT_NATIVE_SHELL__?.version);
}

export function getNativeCapabilities(): NativeCapabilities | null {
  return window.__BILT_NATIVE_SHELL__?.capabilities ?? null;
}

export function nativeShellPlatform(): 'ios_app' | 'android_app' | null {
  const platform = window.__BILT_NATIVE_SHELL__?.platform;
  if (platform === 'ios') return 'ios_app';
  if (platform === 'android') return 'android_app';
  return null;
}

/** True when embedded in the native capability shell. */
export function hasNativeShell(): boolean {
  return isRunningInBiltNativeShell();
}

/** Check a native capability flag from GET_NATIVE_CAPABILITIES. */
export function hasNativeCapability(
  key: keyof Omit<NativeCapabilities, 'native_bridge_version' | 'platform' | 'app_version'>
): boolean {
  const caps = getNativeCapabilities();
  if (!caps) return false;
  return Boolean(caps[key]);
}

/** Gate web features that require a newer native bridge binary. */
export function requiresBridgeVersion(minVersion: number): boolean {
  const version = window.__BILT_NATIVE_SHELL__?.version ?? 0;
  return version >= minVersion;
}

function newBridgeRequestId(prefix: string): string {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function postToNative(payload: BiltNativeRequest, id?: string) {
  if (!window.ReactNativeWebView?.postMessage) return;
  const envelope: BiltBridgeEnvelope = {
    source: 'bilt-web',
    v: NATIVE_BRIDGE_VERSION,
    id,
    payload,
  };
  window.ReactNativeWebView.postMessage(JSON.stringify(envelope));
}

export function setDisplayKeepAwake(enabled: boolean) {
  if (hasNativeCapability('displayKeepAwake')) postToNative({ type: 'DISPLAY_KEEP_AWAKE', enabled });
}

/** Save a PNG, JPEG, or SVG through the installed iOS shell; acknowledge each chunk before sending more. */
export async function saveNativeImage(blob: Blob, filename: string): Promise<'photos' | 'share_sheet'> {
  if (!hasNativeCapability('native_image_save')) throw new Error('Update the Bilt:Spec app to save images.');
  const jpeg = blob.type === 'image/jpeg' && /\.jpe?g$/i.test(filename);
  const png = blob.type === 'image/png' && /\.png$/i.test(filename);
  const svg = blob.type === 'image/svg+xml' && /\.svg$/i.test(filename);
  if (jpeg && !hasNativeCapability('native_image_jpeg')) throw new Error('Update the Bilt:Spec app to save JPEG photos.');
  if (svg && !hasNativeCapability('native_image_svg')) throw new Error('Update the Bilt:Spec app to share SVG images.');
  if (!window.ReactNativeWebView?.postMessage) throw new Error('The iOS save service is unavailable. Reopen the app and try again.');
  if ((!png && !jpeg && !svg) || !blob.size || blob.size > 64 * 1024 * 1024) {
    throw new Error('This image must be a PNG, JPEG, or SVG smaller than 64 MB.');
  }
  const chunkBytes = 147456; // 196608 base64 characters; each chunk decodes independently.
  const chunkCount = Math.ceil(blob.size / chunkBytes);
  const id = newBridgeRequestId('save_image');
  return new Promise((resolve, reject) => {
    let next = 0;
    let settled = false;
    async function sendNext() {
      if (settled) return;
      if (next === chunkCount) {
        postToNative({ type: 'SAVE_IMAGE_FINISH' }, id);
        return;
      }
      const index = next;
      const part = blob.slice(index * chunkBytes, (index + 1) * chunkBytes);
      try {
        const data = await new Promise<string>((res, rej) => {
          const reader = new FileReader();
          reader.onerror = () => rej(new Error('The image could not be read.'));
          reader.onload = () => res(String(reader.result).split(',')[1] || '');
          reader.readAsDataURL(part);
        });
        if (settled) return;
        next += 1;
        postToNative({ type: 'SAVE_IMAGE_CHUNK', index, data }, id);
      } catch {
        finish(new Error('The image could not be read.'));
      }
    }
    const cleanup = installNativeBridgeHost((event, responseId) => {
      if (responseId !== id) return;
      if (event.type === 'NATIVE_ERROR') {
        const code = String(event.code || '');
        finish(new Error(code === 'photos_permission_denied'
          ? 'Photos access is off. Enable it in Settings, then try again.'
          : 'The image could not be saved. Please try again.'));
      } else if (event.type === 'IMAGE_SAVED') {
        finish(null, event.destination as 'photos' | 'share_sheet');
      } else if (event.type === 'IMAGE_SAVE_READY' ||
        (event.type === 'IMAGE_SAVE_CHUNK_ACK' && event.index === next - 1)) {
        void sendNext();
      }
    });
    const timer = window.setTimeout(() => finish(new Error('The app did not finish saving the image. Please try again.')), 120000);
    function finish(error: Error | null, destination?: 'photos' | 'share_sheet') {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      cleanup();
      if (error) reject(error);
      else resolve(destination!);
    }
    postToNative({ type: 'SAVE_IMAGE_BEGIN', filename, byteLength: blob.size, chunkCount }, id);
  });
}

export function requestNativeCapabilities(): void {
  postToNative({ type: 'GET_NATIVE_CAPABILITIES' });
}

let confirmedWebUser: number | null = null;
let scanChromeHidden = false;
let scanHideLockCount = 0;
function syncScanButtonVisibility(): void {
  postToNative({
    type: !confirmedWebUser || scanChromeHidden || scanHideLockCount > 0
      ? 'SCAN_BUTTON_HIDE'
      : 'SCAN_BUTTON_SHOW',
  });
}
export function syncWebAuthState(userId: number | null): void {
  confirmedWebUser = userId;
  postToNative({type: 'WEB_AUTH_STATE', userId});
  // Older released shells still understand the hide message.
  syncScanButtonVisibility();
}

export function requestCardPreview(draft: Record<string, unknown>, context: 'card' | 'scan', back: boolean, quiet: boolean, outcome: 'added' | 'duplicate' | 'pending_offline' | 'pending_review' | 'error'): Promise<void> {
  return new Promise((resolve, reject) => {
    const id = newBridgeRequestId('preview');
    const cleanup = installNativeBridgeHost((event, responseId) => {
      if (responseId !== id) return;
      clearTimeout(timer); cleanup();
      if (event.type === 'CARD_PREVIEW_OPENED') resolve();
      else reject(new Error('Native preview could not open. Use the latest iOS build and an active administrator session.'));
    });
    const timer = setTimeout(() => { cleanup(); reject(new Error('This iOS build did not respond. Update the app to use native preview.')); }, 20000);
    postToNative({type:'PREVIEW_CARD',draft,context,back,quiet,outcome}, id);
  });
}

export function requestNativeLogout(): void {
  postToNative({ type: 'NATIVE_LOGOUT' });
}

type NativeSessionTokens = {
  accessToken: string;
  refreshToken?: string;
  expiresAt: string;
  user: {
    id: number;
    username: string;
    email: string;
    [key: string]: unknown;
  };
};

/** Push web session tokens to native Secure Store (shell WebView only). */
export function syncNativeSession(tokens: NativeSessionTokens): void {
  if (!tokens.accessToken || !tokens.user?.id) return;
  postToNative({
    type: 'SYNC_NATIVE_SESSION',
    session: {
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
      expiresAt: tokens.expiresAt,
      user: tokens.user,
    },
  });
}

export type OpenNativeScannerOptions = {
  mode?: ScanMode;
  /** Required for gate / ticket modes — opens `/organizer/:eventId/scan`. */
  eventId?: number;
};

/**
 * Launch a native QR camera overlay.
 * Passport FAB uses mode passport/auto (default).
 * Organizer gate must pass mode gate|ticket and eventId.
 */
export function openNativeScanner(
  modeOrOptions?: ScanMode | OpenNativeScannerOptions,
  eventIdMaybe?: number
): void {
  let mode: ScanMode | undefined;
  let eventId: number | undefined;
  if (typeof modeOrOptions === 'string' || modeOrOptions === undefined) {
    mode = modeOrOptions;
    eventId = eventIdMaybe;
  } else {
    mode = modeOrOptions.mode;
    eventId = modeOrOptions.eventId;
  }
  const payload: BiltNativeRequest = { type: 'OPEN_SCANNER' };
  if (mode) payload.mode = mode;
  if (typeof eventId === 'number' && Number.isInteger(eventId) && eventId > 0) {
    payload.eventId = eventId;
  }
  postToNative(payload);
}

export function nativeShare(url: string, title?: string): void {
  postToNative({ type: 'SHARE', url, title });
}

export function nativeHaptic(style: 'light' | 'medium' | 'success' | 'warning' | 'error' = 'light'): void {
  postToNative({ type: 'HAPTIC', style });
}

export function setScanButtonHidden(hidden: boolean): void {
  scanChromeHidden = hidden;
  syncScanButtonVisibility();
}

/** Keep the native Scan control clear of web dialogs and mobile drawers. */
export function acquireScanButtonHide(): () => void {
  scanHideLockCount += 1;
  syncScanButtonVisibility();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    scanHideLockCount = Math.max(0, scanHideLockCount - 1);
    syncScanButtonVisibility();
  };
}

export function setScanButtonOffset(bottomPx: number): void {
  postToNative({ type: 'SCAN_BUTTON_OFFSET', bottomPx });
}

export function captureNativePhoto(options: {
  purpose: PhotoPurpose;
  vehicleId?: number;
  modificationId?: number;
  maintenanceId?: number;
  encounterPublicId?: string;
  source?: 'camera' | 'library';
  imageType?: 'hero' | 'gallery' | 'modification';
  selectionLimit?: number;
}): void {
  postToNative({
    type: 'CAPTURE_PHOTO',
    purpose: options.purpose,
    vehicleId: options.vehicleId,
    modificationId: options.modificationId,
    maintenanceId: options.maintenanceId,
    encounterPublicId: options.encounterPublicId,
    source: options.source ?? 'camera',
    imageType: options.imageType,
        selectionLimit: options.selectionLimit,
  });
}

/**
 * Request native camera/library capture and wait for PHOTO_RESULT (or NATIVE_ERROR).
 * Native may upload for some purposes (vehicle_gallery + vehicleId, avatar, …).
 */
export function requestNativePhoto(options: {
  purpose: PhotoPurpose;
  vehicleId?: number;
  modificationId?: number;
  maintenanceId?: number;
  encounterPublicId?: string;
  source?: 'camera' | 'library';
  imageType?: 'hero' | 'gallery' | 'modification';
  selectionLimit?: number;
  timeoutMs?: number;
}): Promise<PhotoBridgePayload> {
  if (!isRunningInBiltNativeShell() || !hasNativeCapability('native_camera')) {
    return Promise.resolve({ ok: false, error: 'native_camera_unavailable' });
  }

  const id = newBridgeRequestId('photo');
  const timeoutMs = options.timeoutMs ?? 120_000;

  return new Promise((resolve) => {
    let settled = false;
    const finish = (payload: PhotoBridgePayload) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      cleanup();
      resolve(payload);
    };

    const timer = setTimeout(() => finish({ ok: false, error: 'timeout' }), timeoutMs);

    const cleanup = installNativeBridgeHost((event, envelopeId) => {
      if (envelopeId && envelopeId !== id) return;
      if (event.type === 'PHOTO_RESULT') {
        const photo = (event as { payload?: PhotoBridgePayload }).payload;
        finish(photo && typeof photo === 'object' ? photo : { ok: false, error: 'empty_result' });
        return;
      }
      if (event.type === 'NATIVE_ERROR') {
        finish({
          ok: false,
          error: String((event as { code?: string; message?: string }).code || 'native_error'),
        });
      }
    });

    postToNative(
      {
        type: 'CAPTURE_PHOTO',
        purpose: options.purpose,
        vehicleId: options.vehicleId,
        modificationId: options.modificationId,
        maintenanceId: options.maintenanceId,
        encounterPublicId: options.encounterPublicId,
        source: options.source ?? 'camera',
        imageType: options.imageType,
        selectionLimit: options.selectionLimit,
      },
      id
    );
  });
}

/** Hide Expo/RN internals from builders while old app binaries are still in the field. */
export function nativePhotoUserError(
  error: string | undefined,
  fallback = 'Camera capture failed'
): string {
  const raw = (error || '').trim();
  if (!raw) return fallback;
  if (/unsupported formdatapart/i.test(raw) || /unsupported formdata implementation/i.test(raw)) {
    return 'Could not send this photo from the app. Use the file picker below, or try another image.';
  }
  return raw;
}

export function requestNativePushStatus(): void {
  postToNative({ type: 'GET_PUSH_STATUS' });
}

export function requestNativePushPermission(): void {
  postToNative({ type: 'REQUEST_PUSH_PERMISSION' });
}

export function openNativeSystemSettings(): void {
  postToNative({ type: 'OPEN_SYSTEM_SETTINGS' });
}

export function setNativeAppBadge(count: number): void {
  postToNative({ type: 'SET_APP_BADGE', count });
}

export function requestNativeConnectivity(): void {
  postToNative({ type: 'GET_CONNECTIVITY' });
}

export function installNativeBridgeHost(
  onEvent?: (event: BiltNativeEvent, envelopeId?: string) => void
): () => void {
  const handler = (event: MessageEvent) => {
    if (typeof event.data !== 'string') return;
    let parsed: unknown;
    try {
      parsed = JSON.parse(event.data);
    } catch {
      return;
    }
    if (!isBridgeEnvelope(parsed) || parsed.source !== 'bilt-native') return;
    onEvent?.(parsed.payload as BiltNativeEvent, parsed.id);
  };
  window.addEventListener('message', handler);
  document.addEventListener('message', handler as EventListener);
  return () => {
    window.removeEventListener('message', handler);
    document.removeEventListener('message', handler as EventListener);
  };
}
